import React, { useState } from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { useLoginWithCode, useRequestLoginCode } from "@/auth/auth.ts";
import { AUTH_ERROR_MAPPING, toAuthErrorData } from "@/auth/authErrors.ts";
import { CODE_LENGTH, digitsOf, identifierFrom, LoginChannel, maskContact } from "@/auth/loginFlow.ts";
import { useStyles } from "@/hooks/useStyles";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TextField } from "@/uiKit/atoms/TextField.tsx";
import { TextToast } from "@/uiKit/atoms/toast/TextToast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { ShowApiError } from "@/uiKit/molecules/ShowApiError.tsx";

const codeSchema = z.string().check(z.regex(new RegExp(`^\\d{${CODE_LENGTH}}$`)));

export type LoginCodeFormProps = {
  channel: LoginChannel;
  contact: string;
  /** Back to the identifier step, keeping what was already typed. */
  onChangeContact: () => void;
};

/**
 * Exchanges the code the patient received for a session.
 */
export const LoginCodeForm: React.FC<LoginCodeFormProps> = ({ channel, contact, onChangeContact }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const loginWithCode = useLoginWithCode();
  const requestCode = useRequestLoginCode();

  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string>();

  const submit = () => {
    if (!codeSchema.safeParse(code).success) {
      setFieldError("errors.invalidCode");
      return;
    }
    setFieldError(undefined);
    // On success the auth status flips and LoginBouncer swaps this page out.
    loginWithCode.login(code, identifierFrom(channel, contact)).catch(() => setCode(""));
  };

  const resend = () => {
    requestCode
      .request(identifierFrom(channel, contact))
      .then(() => {
        setCode("");
        TextToast.success(t("pages.login.codeSentToast"));
      })
      .catch(() => undefined); // Rendered by ShowApiError below.
  };

  return (
    <form
      noValidate
      className={styles.form}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <Typography variant="subtitle">{t("pages.login.codeTitle")}</Typography>
      <Typography variant="bodySmall">
        {t("pages.login.codeSentTo", { contact: maskContact(channel, contact) })}
      </Typography>
      <TextField
        fullWidth
        autoFocus
        value={code}
        onChange={value => {
          setFieldError(undefined);
          // Capped here rather than with maxLength: the html attribute counts the
          // non-digits this filter strips, so a typo would eat one of the slots.
          setCode(digitsOf(value).slice(0, CODE_LENGTH));
        }}
        label={t("pages.login.codeLabel")}
        placeholder={t("pages.login.codePlaceholder")}
        name="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        error={!!fieldError}
        helperText={fieldError && t(fieldError)}
      />
      <Typography variant="captionSmall">{t("pages.login.codeValidity")}</Typography>
      <ShowApiError
        className={styles.apiError}
        error={toAuthErrorData(loginWithCode.error ?? requestCode.error)}
        errorMapping={AUTH_ERROR_MAPPING}
      />
      <Button variant="contained" type="submit" fullWidth pending={loginWithCode.pending}>
        {t("pages.login.submitCode")}
      </Button>
      <Button variant="text" className={styles.link} onClick={resend} pending={requestCode.pending}>
        {t("pages.login.resendCode")}
      </Button>
      <Button variant="text" className={styles.link} onClick={onChangeContact}>
        {t("pages.login.changeContact")}
      </Button>
    </form>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(6),
    padding: theme.spacing(12),
  }),
  apiError: css({
    height: "auto",
    minHeight: 0,
  }),
  link: css({
    alignSelf: "center",
    textDecoration: "underline",
  }),
});
